import * as _ from 'lodash'
import valueFilters from '../valueFilters';
import validator from '../validator';
import modelFactory, { IModel } from '../modelFactory';
import siteVisitAdditionalInfoModel, { ISiteVisitAdditionalInfo } from './siteVisitAdditionalInfoModel';
import siteVisitEmployeeModel, { ISiteVisitEmployee, SiteVisitEmployeeRole } from './siteVisitEmployeeModel';
import siteVisitJobCostModel, { ISiteVisitJobCost } from './siteVisitJobCostModel';
import statusLogged, { IStatusLogged } from './statusLogged';
import model from './model';
import { IModelSet } from '../modelSet';
import additionalInfoContainer, { IAdditionalInfoContainer } from './additionalInfoContainer';
import siteVisitChecklistItemModel, { IInspectionChecklistItem } from './inspectionChecklistItemModel';
import { IStatusLogItem } from './statusLogItemModel';

export enum SiteVisitType {
	assessment = 'assessment',
	delivery = 'delivery',
	install = 'install', 
	inspection = 'inspection',
	service = 'service'
}

export enum SiteVisitStatus {
	canceled = 'canceled',
	completed = 'completed',
	scheduled = 'scheduled',
	started = 'started'
}

export interface ISiteVisit extends IModel, IStatusLogged<SiteVisitStatus>, IAdditionalInfoContainer<ISiteVisitAdditionalInfo> {
	jobKey: string;
	type: SiteVisitType;
	totalMinutes: number;
	readonly scheduledDate?: Date;
	readonly completedDate?: Date;
	installationQuality?: number;
	inspectionChecklistItems: IModelSet<IInspectionChecklistItem>;
	employees: IModelSet<ISiteVisitEmployee>;
	jobCosts: IModelSet<ISiteVisitJobCost>;
	deliverer: string;
	instructions: string;
	requestHoldbackRelease?: boolean;
	readonly primaryEmployee: ISiteVisitEmployee;
	setPrimaryEmployee(employeeKey: string): void;
}

const siteVisitFactory = modelFactory.createFactory<ISiteVisit>('SiteVisit', _.assign({}, model, statusLogged(SiteVisitStatus.scheduled, true), additionalInfoContainer, {
	jobKey: {
		setFilters: valueFilters.clean,
		rules: validator.required()
	},
	type: {
		setFilters: valueFilters.toString,
		rules: validator.required()
	},
	requestHoldbackRelease: {
		setFilters: valueFilters.toBoolean
	},
	additionalInfo: {
		defaultValue: [],
		setFilters: valueFilters.toModelSet(siteVisitAdditionalInfoModel),
		rules: validator.validModel(siteVisitAdditionalInfoModel.rules())
	},
	inspectionChecklistItems: {
		defaultValue: [],
		setFilters: valueFilters.toModelSet(siteVisitChecklistItemModel),
		rules: validator.validModel(siteVisitChecklistItemModel.rules())
	},
	employees: {
		defaultValue: [],
		setFilters: valueFilters.toModelSet(siteVisitEmployeeModel),
		rules: validator.validModel(siteVisitEmployeeModel.rules())
	},
	jobCosts: {
		defaultValue: [],
		setFilters: valueFilters.toModelSet(siteVisitJobCostModel),
		rules: validator.validModel(siteVisitJobCostModel.rules())
	},
	deliverer: {
		nonSerialized: true,
		setFilters: valueFilters.toString
	},
	_get_deliverer: function () {
		return this.getAdditionalInfoValue('deliverer', '');
	},
	_set_deliverer: function (val) {
		this.setAdditionalInfo('deliverer', val);
	},
	instructions: {
		nonSerialized: true,
		setFilters: valueFilters.toString
	},
	_get_instructions: function () {
		return this.getAdditionalInfoValue('instructions', '');
	},
	_set_instructions: function (val) {
		this.setAdditionalInfo('instructions', val);
	},
	primaryEmployee: {
		nonSerialized: true,
		readonly: true,
	},
	_get_primaryEmployee: function () {
		return this._getEmployeeWithRole(SiteVisitEmployeeRole.primary);
	},
	setPrimaryEmployee: function (employeeKey:string) {
		if(!employeeKey) {
			this.employees.delete(this.primaryEmployee?.key);
		}
		else if (this.primaryEmployee) {
			this.primaryEmployee.employeeKey = employeeKey;
		}
		else {
			this.employees.add({ employeeKey, role: SiteVisitEmployeeRole.primary });
		}
	},
	_getEmployeeWithRole: function (role:SiteVisitEmployeeRole) {
		return _.find(this.employees, { role: role });
	},
	scheduledDate: {
		nonSerialized: true,
		readonly: true,
		rules: [(value, target, prop) => {
			if(target.type === SiteVisitType.inspection) {
				return;
			}
			return validator.required()(value, target, prop)
		}]
	},
	_get_scheduledDate: function() {
		const status = this.getMostRecentStatusByStatus(SiteVisitStatus.scheduled) as IStatusLogItem;

		return status?.effectiveDate ?? status?.dateCreated;
	},
	completedDate: {
		nonSerialized: true,
		readonly: true
	},
	_get_completedDate: function() {
		const status = this.getMostRecentStatusByStatus(SiteVisitStatus.completed) as IStatusLogItem;

		return status?.effectiveDate ?? status?.dateCreated;
	},
	installationQuality: {
		nonSerialized: true,
		setFilters: valueFilters.toNumber
	},
	_get_installationQuality: function () {
		const stringVal = this.getAdditionalInfoValue('installationQuality');
		return stringVal && Number(stringVal);
	},
	_set_installationQuality: function (val) {
		this.setAdditionalInfo('installationQuality', val || undefined);
	},
}));

export default siteVisitFactory;